/** Skripsi lifecycle: updates, stage moves, halt/archive. */

import { createDoc, updateDoc, getById, fetchQuery } from '../service';
import { COL, q, col, where } from '../../collections';
import { utcNowIso } from '../../format';
import { isSequentialMove, stageIndex } from '../../constants';
import { currentPeriode } from './periode';
import type {
  HaltReason,
  ProdiDoc,
  SkripsiDoc,
  SkripsiHistoryDoc,
  SkripsiTeamDoc,
} from '../../types';

/** Update a skripsi record (metadata, judul, status, ...). */
export async function updateSkripsi(id: string, patch: Partial<SkripsiDoc>) {
  return updateDoc<SkripsiDoc>(COL.skripsi, id, patch);
}

/**
 * Parameters required for a non-sequential stage change (jump forward past one
 * or more stages, or any backward move). Restricted to staf (staf_jurusan /
 * staf_fakultas / admin) or the kaprodi of the skripsi's prodi; the reason is
 * persisted on the stage timeline for auditability.
 */
export type StageOverrideOpts = {
  overrideByRole: string;
  /** User id of the actor — needed to confirm a `dosen` is actually the kaprodi. */
  overrideByUserId?: string;
  overrideReason: string;
};

/** True when the actor may perform a non-sequential stage change. */
async function canOverrideStage(cur: SkripsiDoc, opts?: StageOverrideOpts): Promise<boolean> {
  if (!opts || !opts.overrideReason.trim()) return false;
  const role = opts.overrideByRole;
  if (role === 'staf_jurusan' || role === 'staf_fakultas' || role === 'admin') return true;
  if (role === 'dosen' && opts.overrideByUserId) {
    const prodi = await getById<ProdiDoc>(COL.prodi, cur.prodiId);
    if (prodi?.kaprodiId && prodi.kaprodiId === opts.overrideByUserId) return true;
  }
  return false;
}

/**
 * Move a skripsi to a new stage. Sequential forward moves (exactly one step)
 * are always allowed. Any backward move or a forward jump that skips stages
 * requires a staf/kaprodi override with an explicit reason, which is recorded
 * on the target stage's timeline entry.
 */
export async function setStage(id: string, stage: SkripsiDoc['stage'], opts?: StageOverrideOpts) {
  const cur = await getById<SkripsiDoc>(COL.skripsi, id);
  if (!cur) throw new Error('Skripsi tidak ditemukan');

  const sequential = isSequentialMove(cur.stage, stage);
  const isOverride = await canOverrideStage(cur, opts);

  if (!sequential && !isOverride) {
    throw new Error(
      'Perubahan tahap harus berurutan. Lompatan maju atau mundur hanya dapat dilakukan staf/kaprodi dengan menyertakan alasan.',
    );
  }

  const patch: Partial<SkripsiDoc> = { stage };
  // Reaching the final stage archives the skripsi (status -> completed).
  if (stage === 'selesai') patch.status = 'completed';
  // Stamp the reached stage with the tahun ajaran/semester in effect right now.
  const p = await currentPeriode();
  const timeline = { ...(cur.stageTimeline ?? {}) };
  timeline[stage] = {
    at: utcNowIso(),
    tahunAjaran: p.tahunAjaran,
    semester: p.semester,
    index: stageIndex(stage),
    // `isOverride && opts` keeps the override narrowed instead of asserted.
    ...(isOverride && opts
      ? { overriddenBy: opts.overrideByRole, overrideReason: opts.overrideReason.trim() }
      : {}),
  };
  patch.stageTimeline = timeline;
  return updateDoc<SkripsiDoc>(COL.skripsi, id, patch);
}

/**
 * Halt an active skripsi: archives progress into the skripsi history, marks the
 * skripsi cancelled, and deactivates every team assignment. The mahasiswa then
 * restarts from a fresh pengajuan judul.
 */
export async function haltSkripsi(input: {
  skripsiId: string;
  reason: HaltReason;
  note?: string;
  haltedBy: string;
  haltedByRole: string;
  actorId: string;
  actorName?: string;
  relatedEventId?: string;
}) {
  const skripsi = await getById<SkripsiDoc>(COL.skripsi, input.skripsiId);
  if (!skripsi || skripsi.status !== 'active') return;

  const now = utcNowIso();

  const team = await fetchQuery<SkripsiTeamDoc>(
    q(col(COL.skripsiTeam), where('skripsiId', '==', input.skripsiId)),
  );
  for (const t of team.filter((x) => x.status === 'active')) {
    const historyEntry = {
      role: t.role,
      status: 'inactive' as const,
      assignedAt: t.assignedAt,
      endedAt: now,
      note: `Skripsi dihentikan · ${input.reason}`,
    };
    await updateDoc<SkripsiTeamDoc>(COL.skripsiTeam, t.id, {
      status: 'inactive',
      endedAt: now,
      note: `Skripsi dihentikan · ${input.reason}`,
      history: [...(t.history ?? []), historyEntry],
    });
  }

  await createDoc<SkripsiHistoryDoc>(COL.skripsiHistory, {
    skripsiId: skripsi.id,
    mahasiswaId: skripsi.mahasiswaId,
    judul: skripsi.judul,
    stage: skripsi.stage,
    reason: input.reason,
    note: input.note,
    haltedBy: input.haltedBy,
    haltedByRole: input.haltedByRole,
    relatedEventId: input.relatedEventId,
    createdAt: now,
  });

  await updateDoc<SkripsiDoc>(COL.skripsi, skripsi.id, {
    status: 'cancelled',
    updatedAt: now,
  });

  return skripsi.id;
}
